import { useCallback, useEffect, useMemo, useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { useFieldArray, useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { DatePicker } from "@/components/ui/date-picker"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  useIsSavingMembershipApplicationDraft,
  useMembershipApplicationDraft,
  useSaveMembershipApplicationDraft,
  type AffiliationDraftEntry,
  type AwardDraftEntry,
} from "../api/useMembershipApplicationDraft"
import { membershipApplicationSteps } from "../constants"
import { useApplicationSectionConfiguration } from "../hooks/useApplicationConfiguration"
import { RequirementSummaryList } from "./requirement-summary-list"

const affiliationEntrySchema = z.object({
  id: z.string().optional(),
  organisation: z.string().trim().min(2, "Enter the organisation name"),
  role: z.string().trim().max(120, "Keep the role concise").optional(),
  membershipNumber: z.string().trim().max(160, "Keep the membership number concise").optional(),
  since: z.string().optional(),
  notes: z.string().trim().max(260, "Keep notes under 260 characters").optional(),
})

const awardEntrySchema = z.object({
  id: z.string().optional(),
  title: z.string().trim().min(2, "Enter the award or recognition"),
  issuer: z.string().trim().min(2, "Enter the issuing organisation"),
  awardedOn: z.string().optional(),
  description: z.string().trim().max(260, "Keep the description under 260 characters").optional(),
})

const affiliationsDraftSchema = z.object({
  affiliations: z.array(affiliationEntrySchema),
  awards: z.array(awardEntrySchema),
})

const affiliationsFormSchema = affiliationsDraftSchema.refine(
  (data) => data.affiliations.length > 0 || data.awards.length > 0,
  {
    message: "Add at least one professional affiliation or recognition",
    path: ["affiliations"],
  },
)

export type AffiliationsFormValues = z.infer<typeof affiliationsDraftSchema>
type AffiliationsDraftValues = z.infer<typeof affiliationsDraftSchema>

function cryptoId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch (_error) {
    // ignore
  }
  return Math.random().toString(36).slice(2, 12)
}

function mapDraftToFormValues(
  affiliations: AffiliationDraftEntry[],
  awards: AwardDraftEntry[],
  options?: { ensureAffiliation?: boolean },
): AffiliationsDraftValues {
  const { ensureAffiliation = true } = options ?? {}

  return {
    affiliations:
      affiliations.length > 0
        ? affiliations.map((entry) => ({
            id: entry.id,
            organisation: entry.organisation ?? "",
            role: entry.role ?? "",
            membershipNumber: entry.membershipNumber ?? "",
            since: entry.since ?? "",
            notes: entry.notes ?? "",
          }))
        : ensureAffiliation
          ? [createEmptyAffiliation()]
          : [],
    awards:
      awards.length > 0
        ? awards.map((entry) => ({
            id: entry.id,
            title: entry.title ?? "",
            issuer: entry.issuer ?? "",
            awardedOn: entry.awardedOn ?? "",
            description: entry.description ?? "",
          }))
        : [],
  }
}

function mapFormToDraft(values: AffiliationsDraftValues) {
  return {
    affiliations: values.affiliations.map((entry) => ({
      id: entry.id ?? cryptoId(),
      organisation: entry.organisation.trim(),
      role: entry.role?.trim() || undefined,
      membershipNumber: entry.membershipNumber?.trim() || undefined,
      since: entry.since?.trim() || undefined,
      notes: entry.notes?.trim() || undefined,
    })),
    awards: values.awards.map((entry) => ({
      id: entry.id ?? cryptoId(),
      title: entry.title.trim(),
      issuer: entry.issuer.trim(),
      awardedOn: entry.awardedOn?.trim() || undefined,
      description: entry.description?.trim() || undefined,
    })),
  }
}

function createEmptyAffiliation() {
  return {
    id: cryptoId(),
    organisation: "",
    role: "",
    membershipNumber: "",
    since: "",
    notes: "",
  }
}

function createEmptyAward() {
  return {
    id: cryptoId(),
    title: "",
    issuer: "",
    awardedOn: "",
    description: "",
  }
}

export function AffiliationsStep() {
  const navigate = useNavigate()
  const { data: draft } = useMembershipApplicationDraft()
  const saveDraftMutation = useSaveMembershipApplicationDraft()
  const isSavingDraft = useIsSavingMembershipApplicationDraft()

  const categoryId = draft?.category?.categoryId ?? null
  const {
    section,
    isVisible,
    isRequired,
    nextStep,
    categoriesQuery,
    category,
  } = useApplicationSectionConfiguration("affiliations", categoryId)

  useEffect(() => {
    if (categoriesQuery.isLoading) {
      return
    }
    if (!isVisible) {
      const fallbackPath = nextStep?.path ?? membershipApplicationSteps[membershipApplicationSteps.length - 1]?.path
      if (fallbackPath) {
        navigate({ to: fallbackPath, replace: true })
      }
    }
  }, [categoriesQuery.isLoading, isVisible, nextStep, navigate])

  if (!isVisible && !categoriesQuery.isLoading) {
    return null
  }

  const formDefaultValues = useMemo(
    () =>
      mapDraftToFormValues(
        draft?.professionalAffiliations ?? [],
        draft?.professionalAwards ?? [],
        { ensureAffiliation: isRequired },
      ),
    [draft?.professionalAffiliations, draft?.professionalAwards, isRequired],
  )

  const schema = isRequired ? affiliationsFormSchema : affiliationsDraftSchema
  const resolver = useMemo(() => zodResolver(schema), [schema])

  const form = useForm<AffiliationsFormValues>({
    resolver,
    defaultValues: formDefaultValues,
    mode: "onChange",
  })

  const affiliationsFieldArray = useFieldArray({
    control: form.control,
    name: "affiliations",
  })

  const awardsFieldArray = useFieldArray({
    control: form.control,
    name: "awards",
  })

  useEffect(() => {
    form.reset(formDefaultValues)
  }, [formDefaultValues, form])

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipNextAutosaveRef = useRef(true)

  useEffect(() => {
    skipNextAutosaveRef.current = true
  }, [formDefaultValues])

  useEffect(() => {
    const subscription = form.watch((values, info) => {
      if (skipNextAutosaveRef.current) {
        skipNextAutosaveRef.current = false
        return
      }

      if (info?.type !== "change" && info?.type !== "blur") {
        return
      }

      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }

      autosaveTimerRef.current = setTimeout(() => {
        const parsed = affiliationsDraftSchema.safeParse(values)
        if (!parsed.success) {
          return
        }

        const payload = mapFormToDraft(parsed.data)
        saveDraftMutation.mutate({
          professionalAffiliations: payload.affiliations,
          professionalAwards: payload.awards,
        })
      }, 800)
    })

    return () => {
      subscription.unsubscribe()
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
  }, [form, saveDraftMutation])

  const nextStepPath = nextStep?.path

  const handleSubmit = useCallback(
    async (values: AffiliationsFormValues) => {
      const payload = mapFormToDraft(values)
      await saveDraftMutation.mutateAsync({
        professionalAffiliations: payload.affiliations,
        professionalAwards: payload.awards,
      })

      if (nextStepPath) {
        navigate({ to: nextStepPath })
      }
    },
    [navigate, nextStepPath, saveDraftMutation],
  )

  const isBusy = isSavingDraft || saveDraftMutation.isPending

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">
            Professional affiliations & awards
            {!isRequired ? (
              <span className="ml-2 text-sm font-medium text-muted-foreground">Optional</span>
            ) : null}
          </h2>
          <p className="text-sm text-muted-foreground">
            {section?.guidance ??
              "Highlight memberships with professional bodies and recognitions that strengthen your application."}
          </p>
          {!isRequired ? (
            <p className="text-xs text-muted-foreground">
              {category?.categoryName
                ? `${category.categoryName} applicants may skip this section if it does not apply.`
                : "You may skip this section if it does not apply."}
            </p>
          ) : null}
        </div>

        {section?.requirementSummaries && section.requirementSummaries.length > 0 ? (
          <RequirementSummaryList
            title="What to include"
            summaries={section.requirementSummaries}
          />
        ) : null}

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium">Professional bodies</h3>
            <Button
              type="button"
              variant="outline"
              onClick={() => affiliationsFieldArray.append(createEmptyAffiliation())}
            >
              Add affiliation
            </Button>
          </div>

          <div className="space-y-4">
            {affiliationsFieldArray.fields.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  No affiliations recorded yet.
                </CardContent>
              </Card>
            ) : null}

            {affiliationsFieldArray.fields.map((field, index) => (
              <Card key={field.id}>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">Affiliation {index + 1}</CardTitle>
                    <CardDescription>Provide membership identifiers where applicable.</CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => affiliationsFieldArray.remove(index)}
                    className="text-destructive"
                  >
                    Remove
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name={`affiliations.${index}.organisation`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel required>Organisation</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. ICPAU" data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name={`affiliations.${index}.role`}
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>Role / designation (optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Member / Fellow" data-invalid={fieldState.invalid} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`affiliations.${index}.membershipNumber`}
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>Membership / registration no. (optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Member ID" data-invalid={fieldState.invalid} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name={`affiliations.${index}.since`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Member since (optional)</FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select date"
                              disableFuture
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div />
                  </div>

                  <FormField
                    control={form.control}
                    name={`affiliations.${index}.notes`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Notes (optional)</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Add supporting context" data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium">Recognitions & awards</h3>
            <Button type="button" variant="outline" onClick={() => awardsFieldArray.append(createEmptyAward())}>
              Add award
            </Button>
          </div>

          <div className="space-y-4">
            {awardsFieldArray.fields.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-6 text-center text-sm text-muted-foreground">
                  Add awards, recognitions or commendations you have received.
                </CardContent>
              </Card>
            ) : null}

            {awardsFieldArray.fields.map((field, index) => (
              <Card key={field.id}>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">Recognition {index + 1}</CardTitle>
                    <CardDescription>Share relevant accolades that demonstrate professional excellence.</CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => awardsFieldArray.remove(index)}
                    className="text-destructive"
                  >
                    Remove
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name={`awards.${index}.title`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel required>Award / recognition</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Best Risk Manager" data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name={`awards.${index}.issuer`}
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel required>Issuing organisation</FormLabel>
                          <FormControl>
                            <Input placeholder="Organisation" data-invalid={fieldState.invalid} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`awards.${index}.awardedOn`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date (optional)</FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select date"
                              disableFuture
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name={`awards.${index}.description`}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Description (optional)</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Add context for this recognition" data-invalid={fieldState.invalid} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {form.formState.errors.affiliations ? (
          <p className="text-sm text-destructive">{form.formState.errors.affiliations.message}</p>
        ) : null}

        <CardFooter className="flex justify-end">
          <Button type="submit" disabled={isBusy}>
            {isBusy ? "Saving..." : "Continue"}
          </Button>
        </CardFooter>
      </form>
    </Form>
  )
}
